import quickFetch from 'quick-fetch';
import { getChirpEarlyRequestName } from 'rpc-client-utils';
import { loginVerifyEarlyRequestRpc } from '../__generated__/chirp/com/hubspot/portals/platform/app/rpc/earlyRequest/AccessRpc.early';
import type {
  LoginVerifyResponseType,
  LoginVerifyErrorType,
} from '../__generated__/chirp/com/hubspot/portals/platform/app/rpc/AccessRpc';
import { getPortalAccountId, getPortalInstanceId } from './utils/metadata';

import { trackPortalInitialized } from './utils/usageTracker';

export type LoginVerifyResult =
  | { type: 'data'; data: LoginVerifyResponseType }
  | {
      type: 'rpcError';
      rpcError: LoginVerifyErrorType;
      correlationId?: string;
    };

function parseChirpUnion(responseText: string): LoginVerifyResult | undefined {
  const parsed = JSON.parse(responseText);

  if (parsed.type === 'data') {
    return { type: 'data', data: parsed.data };
  }

  if (parsed.type === 'rpcError') {
    return {
      type: 'rpcError',
      rpcError: parsed.rpcError,
      correlationId: parsed.correlationId,
    };
  }

  if (parsed.type === 'internalError') {
    console.error(
      'portalsLoginVerify: CHIRP internal error',
      parsed.internalError
    );
    return undefined;
  }

  console.error(
    'portalsLoginVerify: unexpected CHIRP response',
    parsed.type,
    parsed
  );
  return undefined;
}

export function safeGetPortalsLoginVerifyEarlyRequest(options?: {
  objectId?: number;
}): Promise<LoginVerifyResult | undefined> {
  const requestName = getChirpEarlyRequestName(loginVerifyEarlyRequestRpc, {
    request: {
      accountId: getPortalAccountId(),
      portalInstanceId: getPortalInstanceId(),
      objectId: options?.objectId,
      tolerateInstanceAccessDenied: true,
    },
  });
  const requestState = quickFetch.getRequestStateByName(requestName);

  if (!requestState) {
    return Promise.resolve(undefined);
  }

  return new Promise<LoginVerifyResult | undefined>((resolve) => {
    requestState.whenFinished((response: string) => {
      try {
        resolve(parseChirpUnion(response));
      } catch (e) {
        console.error('portalsLoginVerify: failed to parse response', e);
        resolve(undefined);
      }
    });
    requestState.onError((xhr: XMLHttpRequest) => {
      if (xhr.status === 0) {
        console.error('portalsLoginVerify: network failure');
        resolve(undefined);
        return;
      }
      try {
        resolve(parseChirpUnion(xhr.responseText));
      } catch (e) {
        console.error('portalsLoginVerify: failed to parse error response', e);
        resolve(undefined);
      }
    });
  }).then((result) => {
    if (result?.type === 'data') {
      trackPortalInitialized({
        email: result.data.email,
        isOwner: result.data.userId != null,
      });
    }
    return result;
  });
}
